import { Stepper, TimerCountdown } from '@acp/core-ui';
import {
    Box, HStack, ModalHeader, Text,
} from '@chakra-ui/react';
import { FormProvider } from 'react-hook-form';
import { BookableServiceV2 } from '@acp/shared';
import { ChooseBookingTimeScreen } from './choose-booking-time-screen';
import { ConfirmBookingScreen } from './confirm-booking-screen';
import { ChooseBookingContactsScreen } from './choose-booking-contacts-screen';
import { BookingStep, bookingSteps } from './booking-steps';
import { ConfirmBookingTimeout } from './confirm-booking-timeout';
import { useBookingForm } from '../../hooks/use-booking-form';

const EXPIRED_BOOKING_TIME = 5 * 60; // 5 minutes

export function BookingForm({
    propertyId,
    enableCloseButton = true,
    onClose,
    onBookingCreated,
    confirmButtonText,
    overrideBookableServices,
}: {
    propertyId: number;
    confirmButtonText?: string;
    enableCloseButton?: boolean;
    onClose: () => void;
    onBookingCreated?: () => void;
    overrideBookableServices: BookableServiceV2[] | null;
}) {
    const {
        bookingForm,
        isBookable,
        isResetTimer,
        activeStep,
        onTimeUp,
        handleClose,
        handleBack,
        handleSubmit,
        handleConfirm,
        handleChooseContactScreen,
        handleResetTime,
        confirmBookingMutation,
        confirmTimeOutDisclosure,
    } = useBookingForm({
        propertyId,
        overrideBookableServices,
        onClose,
        onBookingCreated,
    });

    return (
        <>
            <FormProvider {...bookingForm}>
                <ModalHeader>
                    <HStack w="full" justifyContent="space-between">
                        <Text>Book on behalf of tenant</Text>

                        {isBookable && (
                            <TimerCountdown
                                key={isResetTimer ? 'reset' : 'default'}
                                label="Booking time left"
                                initialTime={EXPIRED_BOOKING_TIME}
                                isStartByDefault
                                onTimeRunOut={onTimeUp}
                            />
                        )}
                    </HStack>
                </ModalHeader>

                <Box mt={4} px={6} pb={6}>
                    <Stepper activeStep={activeStep} steps={bookingSteps} />
                </Box>

                {activeStep === BookingStep.ChooseTime && (
                    <ChooseBookingTimeScreen
                        propertyId={propertyId}
                        onClose={handleClose}
                        onSubmit={handleChooseContactScreen}
                        handleResetTime={handleResetTime}
                        enableCloseButton={enableCloseButton}
                        overrideBookableServices={overrideBookableServices}
                    />
                )}
                {activeStep === BookingStep.Contacts && (
                    <ChooseBookingContactsScreen
                        propertyId={propertyId}
                        onSubmit={handleSubmit}
                        onBack={handleBack}
                    />
                )}
                {activeStep === BookingStep.Confirmation && (
                    <ConfirmBookingScreen
                        propertyId={propertyId}
                        onSubmit={handleConfirm}
                        onBack={handleChooseContactScreen}
                        isLoading={confirmBookingMutation.isPending}
                        error={confirmBookingMutation.error}
                        confirmButtonText={confirmButtonText}
                    />
                )}
            </FormProvider>

            <ConfirmBookingTimeout
                confirmTimeOutDisclosure={confirmTimeOutDisclosure}
                handleClose={handleClose}
                handleContinueBooking={onTimeUp}
            />
        </>
    );
}
